export type AppRegion = "UK" | "ROI";

const UK_POSTCODE_REGEX =
  /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;

function normalizeValue(value?: string | null): string {
  return (value || "").trim().toLowerCase();
}

export function isIrelandCountry(country?: string | null): boolean {
  const normalized = normalizeValue(country);
  return (
    normalized === "ireland" ||
    normalized === "republic of ireland" ||
    normalized === "ie" ||
    normalized === "roi"
  );
}

export function resolvePaymentRegion(
  region?: string | null,
  country?: string | null,
): AppRegion {
  const normalizedRegion = normalizeValue(region);
  if (
    isIrelandCountry(country) ||
    normalizedRegion === "roi" ||
    normalizedRegion === "ie"
  ) {
    return "ROI";
  }

  return "UK";
}

export function getCurrencyForRegion(region: AppRegion): "GBP" | "EUR" {
  return region === "ROI" ? "EUR" : "GBP";
}

export function getCountryCodeForRegion(region: AppRegion): "GB" | "IE" {
  return region === "ROI" ? "IE" : "GB";
}

export function isUKPostcode(postcode?: string | null): boolean {
  return UK_POSTCODE_REGEX.test((postcode || "").trim());
}

// Validate a customer-typed contact number for checkout. Region-agnostic — the
// number is accepted & saved exactly as typed regardless of the store region;
// SMS routing on the backend decides separately whether to text it.
// Rules (per Domino's phone spec):
//   • digits only, with an optional single leading "+" for international numbers
//   • no spaces, brackets, or other special characters
//   • max length 16 (the "+" counts toward the 16)
//   • landlines accepted (no mobile-prefix requirement)
// Mirrors the backend validateCustomerPhone().
export function isValidContactNumber(raw: string): boolean {
  const trimmed = (raw || "").trim();
  return trimmed.length > 0 && trimmed.length <= 16 && /^\+?\d+$/.test(trimmed);
}

// Convert the customer-typed phone into E.164 using the store's region.
// Rule (matches backend normalisePhoneForRegion):
//   UK store  + 11 digits starting "0" → +44 + last 10 digits
//   ROI store + 10 digits starting "0" → +353 + last 9 digits
// Already-prefixed +<cc>... numbers are validated as E.164 and passed through.
// Returns null for anything else; the BE will re-validate either way.
export function normalisePhoneForRegion(
  raw: string,
  region: AppRegion,
): string | null {
  const cleaned = (raw || "").trim().replace(/[\s\-()]/g, "");
  if (!cleaned) return null;

  if (cleaned.startsWith("+")) {
    return /^\+[1-9]\d{1,14}$/.test(cleaned) ? cleaned : null;
  }

  if (cleaned.startsWith("0") && /^\d+$/.test(cleaned)) {
    const local = cleaned.slice(1);
    if (region === "UK" && cleaned.length === 11) return `+44${local}`;
    if (region === "ROI" && cleaned.length === 10) return `+353${local}`;
  }

  return null;
}
